'use client'

import { CategoryAccTypes } from 'modules/cart'
import { getProductPrices, getProductUnitDetails, getProperProductForm, getQuantityUnit } from 'modules/products'
import { formatDate } from 'shared/utils/date'

import { SaleOrder } from '../../../types'
import { downloadPdf, initializeFonts } from '../../../utils/pdf'

const LOGO_PATH = '/static/company-logo.jpg'

const convertImageToBuffer = async (imagePath: string): Promise<Buffer> => {
  try {
    const response = await fetch(imagePath)
    const arrayBuffer = await response.arrayBuffer()

    return Buffer.from(arrayBuffer)
  } catch (error) {
    console.error('Failed to convert image to buffer:', error)
    throw new Error('Failed to convert image to buffer')
  }
}

const parsePrice = (html: string): string => {
  if (!html) return ''

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  return doc.body.textContent || doc.body.innerText || ''
}

const formatNumber = (value: number): string => {
  if (typeof Intl === 'object' && Intl.NumberFormat) {
    return new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }

  const parts = value.toFixed(2).split('.')

  return `${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}${
    parts[1] ? `,${parts[1]}` : ''
  }`
}

export const generateOrderPdf = async (
  order: SaleOrder,
  productsByCategory: CategoryAccTypes[]
) => {
  if (!order || !productsByCategory) {
    throw new Error('Invalid order or products data')
  }

  try {
    await initializeFonts()

    const { pdf, Document, Page, Text, View, Image, StyleSheet } = await import(
      '@react-pdf/renderer'
    )

    const logoBuffer = await convertImageToBuffer(LOGO_PATH)

    const styles = StyleSheet.create({
      page: {
        padding: 20,
        fontFamily: 'Montserrat',
        position: 'relative',
        minHeight: '100%'
      },
      logo: {
        width: 140,
        marginBottom: 24
      },
      title: {
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 10,
        textAlign: 'center'
      },
      text: {
        fontSize: 12,
        marginBottom: 5
      },
      boldText: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 5
      },
      section: {
        marginBottom: 24
      },
      row: {
        flexDirection: 'row',
        marginBottom: 5
      },
      col: {
        flexDirection: 'column',
        marginBottom: 5
      },
      subheader: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 5
      },
      label: {
        width: 150,
        fontWeight: 'bold',
        fontSize: 10
      },
      value: {
        flex: 1,
        fontSize: 10
      },
      table: {
        marginTop: 10
      },
      categoryName: {
        fontSize: 10,
        fontWeight: 'bold'
      },
      categoryNameWrapper: {
        flexDirection: 'row',
        backgroundColor: '#F6F6F6',
        justifyContent: 'space-between',
        paddingVertical: 6,
        fontSize: 10,
        marginBottom: 5,
        width: '100%',
        paddingHorizontal: 10
      },
      productRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 5,
        paddingVertical: 5,
        paddingHorizontal: 10
      },
      productNameWrapper: {
        flexDirection: 'column',
        width: '280px'
      },
      productName: {
        fontSize: 10,
        fontWeight: 'bold'
      },
      producerName: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#848484'
      },
      productGrammar: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#848484'
      },
      productQuantity: {
        fontSize: 10,
        fontWeight: 'bold',
        marginLeft: 16,
        alignSelf: 'center'
      },
      productUnitDetails: {
        fontSize: 10,
        fontWeight: 'bold',
        width: '80px',
        marginLeft: 25
      },
      priceWrapper: {
        flexDirection: 'column',
        alignItems: 'center'
      },
      oldPrice: {
        textDecoration: 'line-through',
        fontSize: 9,
        color: '#555555'
      },
      currentPrice: {
        fontWeight: 'bold',
        fontSize: 10
      },
      footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        fontSize: 8,
        color: '#666',
        borderTop: '1pt solid #eee',
        paddingTop: 10,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
      },
      footerLogo: {
        width: 100,
        height: 'auto'
      },
      footerText: {
        textAlign: 'right',
        fontSize: 8,
        lineHeight: 1.2
      }
    })

    const createOrderDocument = async () => {
      const address = `${order.deliveryAddressStreet} ${
        order.deliveryAddressHouseNumber
      }${
        order.deliveryAddressFlatNumber
          ? `/${order.deliveryAddressFlatNumber}`
          : ''
      }, ${order.deliveryAddressCity} ${order.deliveryAddressPostalCode}`

      const { createElement } = await import('react')

      return createElement(
        Document,
        {},
        createElement(
          Page,
          { size: 'A4', style: styles.page },
          createElement(Image, { src: logoBuffer, style: styles.logo }),

          createElement(
            Text,
            { style: styles.title },
            `Zamówienie nr ${order.code}`
          ),

          createElement(
            View,
            { style: styles.section },
            createElement(Text, { style: styles.subheader }, 'Szczegóły'),
            createElement(
              View,
              { style: styles.row },
              createElement(
                View,
                { style: styles.col },
                createElement(
                  Text,
                  { style: styles.label },
                  'Data zamówienia:'
                ),
                createElement(
                  Text,
                  { style: styles.value },
                  formatDate(order.createdAt)
                )
              ),
              createElement(
                View,
                { style: styles.col },
                createElement(Text, { style: styles.label }, 'Termin dostawy:'),
                createElement(
                  Text,
                  { style: styles.value },
                  formatDate(order.deliverySlot?.date || '')
                )
              ),
              createElement(
                View,
                { style: styles.col },
                createElement(
                  Text,
                  { style: styles.label },
                  'Godzina dostawy:'
                ),
                createElement(
                  Text,
                  { style: styles.value },
                  `${order.deliverySlot?.start} - ${order.deliverySlot?.end}`
                )
              )
            )
          ),

          createElement(
            View,
            { style: styles.section },
            createElement(Text, { style: styles.boldText }, 'Dane klienta:'),
            createElement(
              View,
              { style: styles.row },
              createElement(
                View,
                { style: styles.col },
                createElement(
                  Text,
                  { style: styles.label },
                  'Nazwisko i imię:'
                ),
                createElement(
                  Text,
                  { style: styles.value },
                  order.deliveryAddressName
                )
              ),
              createElement(
                View,
                { style: styles.col },
                createElement(Text, { style: styles.label }, 'Numer telefonu:'),
                createElement(
                  Text,
                  { style: styles.value },
                  order.deliveryAddressPhoneNumber
                )
              ),
              createElement(
                View,
                { style: styles.col },
                createElement(Text, { style: styles.label }, 'Adres:'),
                createElement(Text, { style: styles.value }, address)
              )
            )
          ),

          createElement(
            View,
            { style: styles.section },
            createElement(
              Text,
              { style: styles.boldText },
              'Informacje o płatności:'
            ),
            createElement(
              View,
              { style: styles.row },
              createElement(
                View,
                { style: styles.col },
                createElement(
                  Text,
                  { style: styles.label },
                  'Metoda płatności:'
                ),
                createElement(
                  Text,
                  { style: styles.value },
                  order.paymentMethod.name
                )
              ),
              createElement(
                View,
                { style: styles.col },
                createElement(Text, { style: styles.label }, 'Data płatności:'),
                createElement(
                  Text,
                  { style: styles.value },
                  formatDate(order.paidAt)
                )
              ),
              createElement(
                View,
                { style: styles.col },
                createElement(Text, { style: styles.label }, 'Kwota:'),
                createElement(
                  Text,
                  { style: styles.value },
                  `${formatNumber(order.paymentValue.value / 100)} ${order.paymentValue.symbol}`
                )
              )
            )
          ),

          createElement(
            View,
            { style: styles.section },
            createElement(Text, { style: styles.boldText }, 'Produkty:'),
            createElement(
              View,
              { style: styles.table },
              ...productsByCategory.map((category, index) =>
                createElement(
                  View,
                  { key: index },
                  createElement(
                    View,
                    { style: styles.categoryNameWrapper },
                    createElement(
                      Text,
                      { style: styles.categoryName },
                      category.categoryName
                    ),
                    createElement(
                      Text,
                      {},
                      getProperProductForm(category.items.length)
                    )
                  ),

                  ...category.items.map(item => {
                    const { product } = item
                    const unitDetails = getProductUnitDetails(product)
                    const { currentPrice, promoPrice, defaultPrice } =
                      getProductPrices({
                        ...product,
                        specialOfferPricing: {
                          priceGross: {
                            formatted:
                              item.specialOfferValueGross?.formatted || '',
                            value: item.specialOfferValueGross?.value || 0,
                            symbol: item.specialOfferValueGross?.symbol || ''
                          }
                        },
                        pricing: {
                          priceGross: {
                            formatted: item.valueGross?.formatted || '',
                            value: item.valueGross?.value || 0,
                            symbol: item.valueGross?.symbol || ''
                          }
                        }
                      })

                    return createElement(
                      View,
                      { key: item.id, style: styles.productRow },
                      createElement(
                        View,
                        { style: styles.row },
                        createElement(
                          View,
                          { style: styles.productNameWrapper },
                          createElement(
                            Text,
                            { style: styles.producerName },
                            item.product?.producer?.name
                          ),
                          createElement(
                            Text,
                            { style: styles.productName },
                            item.product.name
                          ),
                          createElement(
                            Text,
                            { style: styles.productGrammar },
                            unitDetails
                          )
                        ),
                        createElement(
                          Text,
                          { style: styles.productQuantity },
                          getQuantityUnit(item.orderedQuantity)
                        )
                      ),
                      createElement(
                        View,
                        { style: styles.priceWrapper },
                        ...(promoPrice
                          ? [
                              createElement(
                                Text,
                                { style: styles.oldPrice },
                                parsePrice(defaultPrice)
                              )
                            ]
                          : []),
                        createElement(
                          Text,
                          { style: styles.currentPrice },
                          parsePrice(currentPrice)
                        )
                      )
                    )
                  })
                )
              )
            )
          ),

          createElement(
            View,
            { style: styles.footer },
            createElement(Image, { src: logoBuffer, style: styles.footerLogo }),
            createElement(
              View,
              { style: styles.footerText },
              createElement(
                Text,
                {},
                'Lokalny Rolnik sp. z o.o., ul. Koczarska 11, 05-082 Stare Babice, Polska'
              ),
              createElement(
                Text,
                {},
                'REGON: 146701471 | NIP: 5272694344 | KRS: 0000453134'
              )
            )
          )
        )
      )
    }

    const blob = await pdf(await createOrderDocument()).toBlob()

    await downloadPdf(blob)
  } catch (error) {
    console.error('Failed to generate PDF:', error)
    throw new Error('Failed to generate PDF document')
  }
}
